import * as S from './styles'

import {
  Pagination,
  SectionProducts,
  SectionProductsList,
  TabList,
} from '../../../components'
import {
  useCrud,
  usePaginationSearchParams,
  useQueryParams,
  KeycloakHelper,
  http,
} from '@monorepo/infra'

import { Icon, PageSpinner } from '@monorepo/components'
import { ImportLink, PaginationDetails, ExportXlsLink } from '../../atoms'
import { Pagination as PaginationType } from '@monorepo/interfaces'
import { Product } from '@monorepo/interfaces'
import { SearchTerm } from '../../molecules'
import { useHistory } from 'react-router-dom'
import { useState } from 'react'
import useTextTranslation from '@monorepo/infra/build/locales/i18next/useLocation'
import { useKeycloak } from '@react-keycloak/web'
import { DropzoneArea } from 'material-ui-dropzone'
import axios from 'axios'
import BulkDeleteLink from '../../atoms/BulkDeleteLink'
import { exportXls } from '../Utils'
import MediaSelector from '../../organisms/MediaSelector'
import { Avatar, IconButton, Box, Typography, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Card, CardContent, Chip } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import DeleteIcon from '@material-ui/icons/Delete'

type ProductsFetch = PaginationType & {
  data: Product[]
}

export type UploadError = {
  line: number
  sku: string
  message: string
}

export type ImportError = {
  sku: string
  rowIndex: number
  field: string
  value: string
  errorType: string
  message: string
}

export type ImportProductResponse = {
  success: boolean
  processedCount: number
  queuedCount: number
  errors: ImportError[]
  message: string
}

const Products = (): JSX.Element => {
  const { keycloak } = useKeycloak()
  const productBaseURL = '/product'
  const pageURLKey = 'p'
  const searchURLKey = 's'

  const queryParams = useQueryParams()

  const [searchTerm, setSearchTerm] = useState(
    queryParams.get(searchURLKey) ?? ''
  )
  const [uploadErrors, setUploadErrors] = useState<UploadError[]>([])
  const [showDialog, setShowDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [showImportMediaSelector, setShowImportMediaSelector] = useState(false)
  const [selectedMediaFile, setSelectedMediaFile] = useState<{ _id: string; path: string; name: string; mimetype?: string } | null>(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success')
  
  // New state for import results
  const [importResults, setImportResults] = useState<ImportProductResponse | null>(null)
  const [showResultsDialog, setShowResultsDialog] = useState(false)
  const { t } = useTextTranslation('admin')
  const history = useHistory()

  const { apiSearchParams, handlePageChange, handlePrevPage, handleNextPage } =
    usePaginationSearchParams({
      pageAPIKey: 'pageNum',
      pageURLKey,
      searchAPIKey: 'search',
      searchURLKey,
      searchTerm,
    })

  const apiURL = `${productBaseURL}?${apiSearchParams}`
  const isFeaturedProducts = /featured/i.test(apiURL)

  const { fetch } = useCrud<Product>(apiURL, '_id', {
    revalidateOnFocus: false,
  })

  const { data, loading } = fetch
  const { data: products, ...pagination } = data as ProductsFetch
  pagination.page = Number(pagination.page)

  const handleAddProduct = () => history.push('products/create')

  const handleClearSearchTerm = () => setSearchTerm('')

  const handleUploadFile = async (file: File | null) => {
    if (!file && !selectedMediaFile) return
    

    const url = `${process.env.REACT_APP_REST_BASE_URL}/import-product`

    try {
      if (selectedMediaFile) {
        setIsUploading(true)
        const response = await http.post({
          url,
          data: { fileUrl: selectedMediaFile.path },
          headers: {
            Authorization: `Bearer ${KeycloakHelper.getToken(keycloak)}`,
            'content-type': 'application/json',
          },
        })
        setIsUploading(false)
        const status = (response)?.status
        if (status === 201) {
          // Parse the response data
          const responseData = response.data as ImportProductResponse
          setImportResults(responseData)
          
          // Check if there are errors to show
          if (responseData.errors && responseData.errors.length > 0) {
            setShowResultsDialog(true)
          } else {
            setShowResultsDialog(true)
            setSnackbarMessage('Bulk Product Import Successfully Started')
            setSnackbarSeverity('success')
            setSnackbarOpen(true)
          }
          
          setShowDialog(false)
          setSelectedMediaFile(null)
          setFile(null)
        } else {
          setSnackbarMessage('Bulk Product Import Failed')
          setSnackbarSeverity('error')
          setSnackbarOpen(true)
        }
        return
      }

      if (file) {
        setIsUploading(true)
        // New file via Dropzone: send multipart form-data
        const formData = new FormData()
        formData.append('file', file)
        const config = {
          headers: {
            Authorization: `Bearer ${KeycloakHelper.getToken(keycloak)}`,
            'content-type': 'multipart/form-data',
          },
        }
        const success = await axios.post(url, formData, config)
        setIsUploading(false)
        if (success.status === 201) {
          // Parse the response data
          const responseData = success.data as ImportProductResponse
          setImportResults(responseData)
          
          // Check if there are errors to show
          if (responseData.errors && responseData.errors.length > 0) {
            setShowResultsDialog(true)
          } else {
            setSnackbarMessage('Bulk Product Import Successfully Started')
            setSnackbarSeverity('success')
            setSnackbarOpen(true)
          }
          
          setShowDialog(false)
          setSelectedMediaFile(null)
          setFile(null)
        } else {
          setSnackbarMessage('Bulk Product Import Failed')
          setSnackbarSeverity('error')
          setSnackbarOpen(true)
          setFile(null)
        }
        return
      }
    } catch (error) {
      setIsUploading(false)
      setSnackbarMessage('Import failed')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    }
  }

  const handleUploadDeleteFile = (file: File | null) => {
    if (file) {
      setIsUploading(true)
      axios.create({
        baseURL: process.env.REACT_APP_REST_BASE_URL,
      })
      const url = `${process.env.REACT_APP_REST_BASE_URL}/product/bulk-delete`
      const formData = new FormData()
      formData.append('file', file)
      const config = {
        headers: {
          Authorization: `Bearer ${KeycloakHelper.getToken(keycloak)}`,
          'content-type': 'multipart/form-data',
        },
      }
      axios
        .post(url, formData, config)
        .then((success) => {
          setIsUploading(false)
          setShowDeleteDialog(false)
          history.go(0)
        })
        .catch((error) => {
          setIsUploading(false)
          setShowDeleteDialog(false)
        })
    }
  }

  const open = () => setShowDialog(true)
  const close = () => {
    setShowDialog(false)
    setUploadErrors([])
    setSelectedMediaFile(null)
  }
  const openDeleteDialog = () => setShowDeleteDialog(true)
  const closeDeleteDialog = () => setShowDeleteDialog(false)

  const handleExportXls = () => {
    exportXls(
      `${process.env.REACT_APP_REST_BASE_URL}/product/export`,
      'product-export.xlsx',
      KeycloakHelper.getToken(keycloak)
    )
  }

  const getDialog = () => {
    return (
      <S.Dialog
        aria-label="Import Products"
        isOpen={showDialog}
        onDismiss={close}
      >
        <S.CloseButton className="close-button" onClick={close}>
          x
        </S.CloseButton>
        <S.DialogTitle>Import products</S.DialogTitle>
        <S.DialogText>
          Select an existing uploaded file to import:
        </S.DialogText>
        <div style={{ marginBottom: 12 }}>
          <S.UploadButton
            label={`Select From Existing Import Files`}
            onClick={() => {
              setShowDialog(false);              // close Reach dialog
              setShowImportMediaSelector(true);  // open MUI dialog
            }}
          />
        </div>
        {selectedMediaFile && (
          <Box style={{ 
            marginBottom: 12, 
            padding: 12, 
            backgroundColor: '#f5f5f5', 
            borderRadius: 8,
            border: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <Avatar
              src={selectedMediaFile.mimetype?.startsWith('image/') ? selectedMediaFile.path : undefined}
              variant="rounded"
              style={{ width: 50, height: 50 }}
            >
              {!selectedMediaFile.mimetype?.startsWith('image/') && getFileIcon(selectedMediaFile.mimetype)}
            </Avatar>
            <Box style={{ flex: 1 }}>
              <Typography variant="body2" style={{ fontWeight: 500 }}>
                {selectedMediaFile.name.split('@')[1] ?? selectedMediaFile.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Selected file
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setSelectedMediaFile(null)}
              style={{ color: '#f44336' }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        )}
        {/* <DropzoneArea
          maxFileSize={100000000}
          filesLimit={1}
          onChange={(files) => {
            setFile(files[0])
          }}
        ></DropzoneArea> */}
        <S.DialogText>
          Any current products that have the same SKU will not be overwritten.
        </S.DialogText>
        {uploadErrors.length > 0 && (
          <>
            <S.DialogError>
              There are errors with your submission. SKUs with errors will only
              be imported if they contain new variants. If you have previously
              uploaded this file and are missing variants, and missing variants
              will be imported, as well.
            </S.DialogError>
            {uploadErrors.map((uploadError) => {
              return (
                <S.DialogError>{`Line ${uploadError.line}: ${uploadError.message}`}</S.DialogError>
              )
            })}
          </>
        )}
        <S.DialogText>
          <S.DialogLink
            href="https://theindustrious.atlassian.net/wiki/external/2147221505/MDQ1YjYyNTg1MmI5NGFmYWI1MDE5ZGZkODkwOWI4OTY?atlOrigin=eyJpIjoiOGE0NzRjODhkNTkwNGNmYWE5NDdjYTUzZDY3ZTA5ZDYiLCJwIjoiYyJ9"
            target="blank"
          >
            <Icon icon="BooksVertical" size={18} />
            Instructions (for reference)
          </S.DialogLink>
        </S.DialogText>
        <S.ButtonRow>
          <S.CancelButton
            label={`Cancel`}
            colorOption="stroke"
            onClick={close}
            disabled={isUploading}
          />
          <S.UploadButton
            label={isUploading ? `Uploading...` : `Upload and continue`}
            disabled={isUploading}
            onClick={() => handleUploadFile(file)}
          />
        </S.ButtonRow>
        {isUploading && <PageSpinner />}
      </S.Dialog>
    )
  }

  const getDeleteDialog = () => {
    return (
      <S.Dialog
        aria-label="Bulk Delete Products"
        isOpen={showDeleteDialog}
        onDismiss={closeDeleteDialog}
      >
        <S.CloseButton className="close-button" onClick={closeDeleteDialog}>
          x
        </S.CloseButton>
        <S.DialogTitle>Bulk Delete Products</S.DialogTitle>
        <DropzoneArea
          maxFileSize={100000000}
          filesLimit={1}
          onChange={(files) => {
            setFile(files[0])
          }}
        ></DropzoneArea>
        <S.DialogText>
          Please upload an Excel file (.xlsx) with one worksheet titled "Sheet1"
          which contains the word "SKU" in cell A1 and a list of SKUs, one per
          row, in column A
        </S.DialogText>
        <S.ButtonRow>
          <S.CancelButton
            label={`Cancel`}
            colorOption="stroke"
            onClick={closeDeleteDialog}
            disabled={isUploading}
          />
          <S.UploadButton
            label={isUploading ? `Uploading...` : `Upload and continue`}
            disabled={isUploading}
            onClick={() => handleUploadDeleteFile(file)}
          />
        </S.ButtonRow>
        {isUploading && <PageSpinner />}
      </S.Dialog>
    )
  }

  const getImportResultsDialog = () => {
    if (!importResults) return null

    return (
      <Dialog
        open={showResultsDialog}
        onClose={() => setShowResultsDialog(false)}
        maxWidth="lg"
        fullWidth
        aria-labelledby="import-results-dialog-title"
      >
        <DialogTitle id="import-results-dialog-title">
          Import Results
        </DialogTitle>
        <DialogContent>
          {/* Summary Cards */}
          <Box display="flex" mb={3} style={{ gap: '16px' }}>
            <Card style={{ flex: 1, backgroundColor: '#e3f2fd' }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  Product Rows Extracted
                </Typography>
                <Typography variant="h4" color="primary">
                  {importResults.processedCount}
                </Typography>
              </CardContent>
            </Card>
            <Card style={{ flex: 1, backgroundColor: '#e8f5e8' }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  Product Rows Created
                </Typography>
                <Typography variant="h4" color="primary">
                  {importResults.queuedCount}
                </Typography>
              </CardContent>
            </Card>
            <Card style={{ flex: 1, backgroundColor: '#ffebee' }}>
              <CardContent>
                <Typography variant="h6" color="primary">
                  Issues Found
                </Typography>
                <Typography variant="h4" color="primary">
                  {importResults.errors.length}
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Message */}
          <Box mb={3}>
            <Typography variant="body1" color="textSecondary">
              {importResults.message}
            </Typography>
          </Box>

          {/* Errors Table */}
          {importResults.errors.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Issues of Imported File
              </Typography>
              <TableContainer component={Paper} style={{ maxHeight: 400 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>SKU</TableCell>
                      {/* <TableCell>Row Index</TableCell> */}
                      <TableCell>Field</TableCell>
                      <TableCell>Faulty Value</TableCell>
                      <TableCell>Error Type</TableCell>
                      <TableCell>Message</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {importResults.errors.map((error, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Chip label={error.sku} size="small" style={{backgroundColor:'#ffcccc', color:'#b30000',borderColor:'#b30000'}} variant="outlined"/>
                        </TableCell>
                        {/* <TableCell>{error.rowIndex}</TableCell> */}
                        <TableCell>{error.field == 'MediaIds'? "Drive Media IDs" : error.field}</TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {error.value ?? 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={error.errorType} 
                            size="small" 
                            style={{backgroundColor:'#ffcccc', color:'#b30000',borderColor:'#b30000'}}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" style={{ maxWidth: 300 }}>
                            {error.message}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowResultsDialog(false)} 
            color="primary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    )
  }


  const getFileIcon = (mimetype?: string) => {
    if (mimetype?.startsWith('image/')) {
      return '🖼️'
    } else if (mimetype?.includes('zip')) {
      return '📦'
    } else if (mimetype?.endsWith('sheet')) {
      return '📥'
    } else {
      return '📄'
    }
  }

  const renderImportMediaSelector = () => {
    const handleSelect = (items: { _id: string; path: string; name: string; mimetype?: string }[]) => {
      if (!items || items.length === 0) {
        setShowImportMediaSelector(false)
        setShowDialog(true) // reopen import dialog even if nothing selected (optional)
        return
      }

      const item = items[0]
      setSelectedMediaFile(item)
      setShowImportMediaSelector(false)
      setShowDialog(true)
    }

    return (
      <MediaSelector
        open={showImportMediaSelector}
        onClose={() => {
          setShowImportMediaSelector(false)
          setShowDialog(true) // reopen import dialog on cancel
        }}
        onSelect={handleSelect}
        mediaType="importProduct"
        currentMedia={[]}
        multiple={false}
        keycloak={keycloak}
        hideActions={true}
      />
    )
  }

  return (
    <>
      <S.Main>
        <SectionProducts />
        <S.PageActions>
          <TabList
            tabKey="status"
            tabs={[
              { description: 'All', tabName: 'all' },
              { description: 'Active', tabName: 'active' },
              { description: 'Featured', tabName: 'featured' },
              { description: 'Draft', tabName: 'draft' },
              { description: 'Archived', tabName: 'archived' },
              { description: 'Expired', tabName: 'expired' },
            ]}
          />
          <S.ActionButtonsContainer>
            <ExportXlsLink onClick={handleExportXls} />
            <ImportLink onClick={open} />
            <BulkDeleteLink onClick={openDeleteDialog} />
            <S.AddProductButton
              label={t('section_products.button_text')}
              colorOption="blue"
              onClick={handleAddProduct}
            />
          </S.ActionButtonsContainer>
        </S.PageActions>
        <S.Separator />
        <S.SearchFiltersContainer>
          <S.Search
            placeholder={t('section_products_list.input_placeholder')}
            onChange={(e) => setSearchTerm(e.target.value)}
            value={searchTerm}
          />
          <SearchTerm searchTerm={searchTerm} onClear={handleClearSearchTerm} />
          <PaginationDetails
            totalItems={pagination.totalCount}
            totalPages={pagination.totalPages}
            itemsPerPage={pagination.pageSize}
            pageNumber={pagination.page}
            onClickPrev={handlePrevPage}
            onClickNext={handleNextPage}
          />
        </S.SearchFiltersContainer>
        <SectionProductsList
          loading={loading}
          products={products}
          isFeaturedProducts={isFeaturedProducts}
        />
        <S.PaginationContainer>
          <Pagination
            onPageChange={handlePageChange}
            totalCount={pagination.totalCount}
            page={pagination.page}
            pageSize={pagination.pageSize}
            totalPages={pagination.totalPages}
          />
        </S.PaginationContainer>
      </S.Main>
      {getDialog()}
      {getDeleteDialog()}
      {getImportResultsDialog()}
      {renderImportMediaSelector()}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} style={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

export default Products
